<script setup lang="ts">
import type { Component } from 'vue';

defineProps<{
  icon?: Component;
  title: string;
  description?: string;
  /** Reduce padding/font sizes for inline placements */
  compact?: boolean;
}>();
</script>

<template>
  <div class="cn-empty" :class="{ compact }">
    <span v-if="icon" class="cn-empty-disc" aria-hidden="true">
      <component
        :is="icon"
        class="cn-empty-icon"
        :class="compact ? 'nxt-icon-md' : 'nxt-icon-lg'"
      />
    </span>
    <div class="cn-empty-title">{{ title }}</div>
    <div v-if="description" class="cn-empty-desc">{{ description }}</div>
    <div v-if="$slots.default" class="cn-empty-actions">
      <slot />
    </div>
  </div>
</template>

<style scoped>
/* Class names are kept: callers restyle `.cn-empty-title` / `.cn-empty-desc`. */
.cn-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xl) var(--nxt-space-md);
  color: var(--nxt-empty-state-fg);
}
.cn-empty.compact {
  padding: var(--nxt-space-sm) var(--nxt-space-xs);
  gap: var(--nxt-space-2xs);
}

/* The icon sits in a tinted disc — an empty state is an invitation, not a
   greyed-out control. */
.cn-empty-disc {
  display: grid;
  place-items: center;
  width: calc(var(--nxt-size-unit) * 3);
  height: calc(var(--nxt-size-unit) * 3);
  margin-block-end: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: color-mix(in srgb, var(--nxt-brand-bg) 12%, var(--nxt-surface-bg));
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 5)
    color-mix(in srgb, var(--nxt-brand-bg) 5%, transparent);
  color: var(--nxt-brand-bg);
}
.cn-empty.compact .cn-empty-disc {
  width: calc(var(--nxt-size-unit) * 2);
  height: calc(var(--nxt-size-unit) * 2);
  margin-block-end: 0;
  box-shadow: none;
}

.cn-empty-icon {
  color: inherit;
}

.cn-empty-title {
  font-weight: var(--nxt-font-weight-semibold);
  font-size: var(--nxt-font-size-md);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-fg);
}
.cn-empty.compact .cn-empty-title {
  font-size: var(--nxt-font-size-sm);
}

.cn-empty-desc {
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-muted);
  max-width: 42ch;
}
.cn-empty.compact .cn-empty-desc {
  font-size: var(--nxt-font-size-xs);
}

.cn-empty-actions {
  margin-top: var(--nxt-space-xs);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--nxt-space-xs);
}
.cn-empty.compact .cn-empty-actions {
  margin-top: var(--nxt-space-2xs);
}
</style>
